import { Minus, Plus, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';

interface Point {
  x: number;
  y: number;
}

interface RoomAreaEditorProps {
  floorPlanUrl: string;
  initialPoints?: Point[];
  onPointsChange?: (points: Point[]) => void;
  onCrop: (croppedBlob: Blob, points: Point[]) => void;
  disabled?: boolean;
}

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const POINT_RADIUS = 8;
const CLOSE_THRESHOLD = 20;
const MIN_CROP_SIZE = 800;
const TARGET_UPSCALE_SIZE = 1024;

export const RoomAreaEditor: React.FC<RoomAreaEditorProps> = ({
  floorPlanUrl,
  initialPoints = [],
  onPointsChange,
  onCrop,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [points, setPoints] = useState<Point[]>(initialPoints);
  const [isPolygonClosed, setIsPolygonClosed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [draggingPointIndex, setDraggingPointIndex] = useState<number | null>(null);
  const [didDragOrPan, setDidDragOrPan] = useState(false);

  // Load image
  useEffect(() => {
    if (!floorPlanUrl?.trim()) {
      setImageError('No image URL provided');
      setImageLoaded(false);
      return;
    }
    setImageError(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      setImageError(null);
    };
    img.onerror = () => {
      setImageError('Failed to load floor plan image');
      setImageLoaded(false);
    };
    img.src = floorPlanUrl;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [floorPlanUrl]);

  // Draw canvas
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    const img = imageRef.current;

    if (!canvas || !ctx || !img || !imageLoaded) return;

    const container = containerRef.current;
    if (!container) return;

    // Set canvas size to container
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate scaled image dimensions to fit container
    const containerAspect = canvas.width / canvas.height;
    const imageAspect = img.width / img.height;

    let drawWidth: number, drawHeight: number;
    if (imageAspect > containerAspect) {
      drawWidth = canvas.width;
      drawHeight = canvas.width / imageAspect;
    } else {
      drawHeight = canvas.height;
      drawWidth = canvas.height * imageAspect;
    }

    // Apply zoom and pan
    const scaledWidth = drawWidth * zoom;
    const scaledHeight = drawHeight * zoom;
    const offsetX = (canvas.width - scaledWidth) / 2 + pan.x;
    const offsetY = (canvas.height - scaledHeight) / 2 + pan.y;

    // Draw image
    ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

    // Draw polygon lines
    if (points.length > 0) {
      ctx.beginPath();
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);

      const firstPoint = canvasToScreen(points[0], canvas, drawWidth, drawHeight);
      ctx.moveTo(firstPoint.x, firstPoint.y);

      for (let i = 1; i < points.length; i++) {
        const p = canvasToScreen(points[i], canvas, drawWidth, drawHeight);
        ctx.lineTo(p.x, p.y);
      }

      if (isPolygonClosed) {
        ctx.closePath();
        ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
        ctx.fill();
      }

      ctx.stroke();
    }

    // Draw points
    points.forEach((point, index) => {
      const screenPoint = canvasToScreen(point, canvas, drawWidth, drawHeight);
      ctx.beginPath();
      ctx.arc(screenPoint.x, screenPoint.y, POINT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = index === 0 ? '#22c55e' : '#3b82f6';
      ctx.fill();
      ctx.strokeStyle = draggingPointIndex === index ? '#fbbf24' : '#fff';
      ctx.lineWidth = draggingPointIndex === index ? 3 : 2;
      ctx.stroke();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, zoom, pan, imageLoaded, isPolygonClosed, draggingPointIndex]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => draw());
    observer.observe(container);
    return () => observer.disconnect();
  }, [draw]);

  // Convert normalized coords (0-1) to screen coords
  const canvasToScreen = (point: Point, canvas: HTMLCanvasElement, drawWidth: number, drawHeight: number): Point => {
    const scaledWidth = drawWidth * zoom;
    const scaledHeight = drawHeight * zoom;
    const offsetX = (canvas.width - scaledWidth) / 2 + pan.x;
    const offsetY = (canvas.height - scaledHeight) / 2 + pan.y;

    return {
      x: offsetX + point.x * scaledWidth,
      y: offsetY + point.y * scaledHeight,
    };
  };

  // Convert screen coords to normalized coords (0-1)
  const screenToCanvas = (screenX: number, screenY: number): Point | null => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return null;

    const containerAspect = canvas.width / canvas.height;
    const imageAspect = img.width / img.height;

    let drawWidth: number, drawHeight: number;
    if (imageAspect > containerAspect) {
      drawWidth = canvas.width;
      drawHeight = canvas.width / imageAspect;
    } else {
      drawHeight = canvas.height;
      drawWidth = canvas.height * imageAspect;
    }

    const scaledWidth = drawWidth * zoom;
    const scaledHeight = drawHeight * zoom;
    const offsetX = (canvas.width - scaledWidth) / 2 + pan.x;
    const offsetY = (canvas.height - scaledHeight) / 2 + pan.y;

    const x = (screenX - offsetX) / scaledWidth;
    const y = (screenY - offsetY) / scaledHeight;

    // Check bounds
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;

    return { x, y };
  };

  // Find point at screen position
  const findPointAtPosition = (screenX: number, screenY: number): number | null => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return null;

    const containerAspect = canvas.width / canvas.height;
    const imageAspect = img.width / img.height;
    let drawWidth: number, drawHeight: number;
    if (imageAspect > containerAspect) {
      drawWidth = canvas.width;
      drawHeight = canvas.width / imageAspect;
    } else {
      drawHeight = canvas.height;
      drawWidth = canvas.height * imageAspect;
    }

    for (let i = 0; i < points.length; i++) {
      const screenPoint = canvasToScreen(points[i], canvas, drawWidth, drawHeight);
      const distance = Math.sqrt(
        Math.pow(screenX - screenPoint.x, 2) + Math.pow(screenY - screenPoint.y, 2)
      );
      if (distance <= POINT_RADIUS + 4) {
        return i;
      }
    }
    return null;
  };

  // Handle canvas click
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Ignore click if we just finished dragging or panning
    if (disabled || didDragOrPan) {
      setDidDragOrPan(false);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Don't add points if polygon is closed
    if (isPolygonClosed) return;

    // Check if clicking near first point to close polygon (check this BEFORE point drag check)
    if (points.length >= 3) {
      const img = imageRef.current;
      if (img) {
        const containerAspect = canvas.width / canvas.height;
        const imageAspect = img.width / img.height;
        let drawWidth: number, drawHeight: number;
        if (imageAspect > containerAspect) {
          drawWidth = canvas.width;
          drawHeight = canvas.width / imageAspect;
        } else {
          drawHeight = canvas.height;
          drawWidth = canvas.height * imageAspect;
        }

        const firstScreenPoint = canvasToScreen(points[0], canvas, drawWidth, drawHeight);
        const distance = Math.sqrt(
          Math.pow(x - firstScreenPoint.x, 2) + Math.pow(y - firstScreenPoint.y, 2)
        );

        if (distance < CLOSE_THRESHOLD) {
          setIsPolygonClosed(true);
          onPointsChange?.(points);
          return;
        }
      }
    }

    // Check if clicking on existing point (don't add new point, just ignore - drag handles it)
    const pointIndex = findPointAtPosition(x, y);
    if (pointIndex !== null) return;

    const normalizedPoint = screenToCanvas(x, y);
    if (!normalizedPoint) return;

    const newPoints = [...points, normalizedPoint];
    setPoints(newPoints);
    onPointsChange?.(newPoints);
  };

  // Mouse handlers for panning and dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setDidDragOrPan(false);

    // Left click
    if (e.button === 0) {
      // Check if clicking on a point to drag it
      const pointIndex = findPointAtPosition(x, y);
      if (pointIndex !== null) {
        e.preventDefault();
        setDraggingPointIndex(pointIndex);
        canvas.style.cursor = 'grabbing';
        return;
      }

      // Start panning on empty area (when zoomed in) or with Shift key
      if (zoom > 1 || e.shiftKey) {
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        canvas.style.cursor = 'move';
        return;
      }
    }

    // Middle mouse button always pans
    if (e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      canvas.style.cursor = 'move';
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Dragging a point
    if (draggingPointIndex !== null) {
      setDidDragOrPan(true);
      const normalizedPoint = screenToCanvas(x, y);
      if (normalizedPoint) {
        const newPoints = [...points];
        newPoints[draggingPointIndex] = normalizedPoint;
        setPoints(newPoints);
      }
      return;
    }

    // Panning
    if (isPanning) {
      setDidDragOrPan(true);
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      return;
    }

    // Update cursor based on hover and zoom
    const pointIndex = findPointAtPosition(x, y);
    if (pointIndex !== null) {
      canvas.style.cursor = 'grab';
    } else if (zoom > 1) {
      canvas.style.cursor = 'move';
    } else {
      canvas.style.cursor = 'crosshair';
    }
  };

  const handleMouseUp = () => {
    const canvas = canvasRef.current;

    if (draggingPointIndex !== null) {
      onPointsChange?.(points);
      setDraggingPointIndex(null);
    }
    setIsPanning(false);

    // Reset cursor
    if (canvas) {
      canvas.style.cursor = 'crosshair';
    }
  };

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setZoom((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z + delta)));
  };

  // Zoom controls
  const handleZoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
  const handleZoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Clear points
  const handleClearPoints = () => {
    setPoints([]);
    setIsPolygonClosed(false);
    onPointsChange?.([]);
  };

  // Undo last point
  const handleUndoLastPoint = () => {
    if (points.length === 0) return;

    // If polygon is closed, reopen it
    if (isPolygonClosed) {
      setIsPolygonClosed(false);
      return;
    }

    const newPoints = points.slice(0, -1);
    setPoints(newPoints);
    onPointsChange?.(newPoints);
  };

  // Upscale if needed
  const upscaleIfNeeded = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
    if (canvas.width >= MIN_CROP_SIZE && canvas.height >= MIN_CROP_SIZE) {
      return canvas;
    }

    const upscaled = document.createElement('canvas');
    upscaled.width = TARGET_UPSCALE_SIZE;
    upscaled.height = TARGET_UPSCALE_SIZE;
    const ctx = upscaled.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, TARGET_UPSCALE_SIZE, TARGET_UPSCALE_SIZE);
    return upscaled;
  };

  // Crop image
  const cropImage = (): HTMLCanvasElement | null => {
    const img = imageRef.current;
    if (!img || points.length < 3) return null;

    // Calculate bounding box
    const xs = points.map((p) => p.x * img.width);
    const ys = points.map((p) => p.y * img.height);
    const minX = Math.max(0, Math.floor(Math.min(...xs)));
    const maxX = Math.min(img.width, Math.ceil(Math.max(...xs)));
    const minY = Math.max(0, Math.floor(Math.min(...ys)));
    const maxY = Math.min(img.height, Math.ceil(Math.max(...ys)));

    const cropWidth = maxX - minX;
    const cropHeight = maxY - minY;

    // Create crop canvas
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = cropWidth;
    cropCanvas.height = cropHeight;
    const ctx = cropCanvas.getContext('2d')!;

    // Create clipping path
    ctx.beginPath();
    const firstPoint = { x: points[0].x * img.width - minX, y: points[0].y * img.height - minY };
    ctx.moveTo(firstPoint.x, firstPoint.y);
    for (let i = 1; i < points.length; i++) {
      const p = { x: points[i].x * img.width - minX, y: points[i].y * img.height - minY };
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.clip();

    // Draw image
    ctx.drawImage(img, -minX, -minY);

    return upscaleIfNeeded(cropCanvas);
  };

  // Save crop
  const handleSaveCrop = () => {
    const croppedCanvas = cropImage();
    if (!croppedCanvas) return;

    croppedCanvas.toBlob((blob) => {
      if (blob) {
        onCrop(blob, points);
      }
    }, 'image/png');
  };

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground w-16 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleResetView}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUndoLastPoint}
            disabled={points.length === 0}
            title="Undo last point (or reopen closed polygon)"
          >
            <Undo2 className="h-4 w-4 mr-1" />
            Undo
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClearPoints}
            disabled={points.length === 0}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={containerRef}
        className="relative border rounded-lg bg-muted overflow-hidden"
        style={{ height: 400 }}
      >
        {!imageLoaded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            {imageError ? (
              <span className="text-destructive text-sm">{imageError}</span>
            ) : (
              <span className="text-muted-foreground">Loading floor plan...</span>
            )}
          </div>
        )}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 cursor-crosshair"
          onClick={handleCanvasClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />
      </div>

      {/* Instructions */}
      <p className="text-sm text-muted-foreground">
        {isPolygonClosed
          ? 'Room area defined. Drag points to adjust, or click Undo to reopen.'
          : points.length === 0
          ? 'Click to add points. Scroll to zoom, drag to pan when zoomed in.'
          : points.length < 3
          ? `Add ${3 - points.length} more point${3 - points.length > 1 ? 's' : ''}. Drag points to adjust.`
          : 'Click near the green point to close, or drag points to adjust.'}
      </p>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          type="button"
          onClick={handleSaveCrop}
          disabled={!isPolygonClosed || points.length < 3 || disabled}
        >
          Save Crop
        </Button>
      </div>
    </div>
  );
};

export default RoomAreaEditor;
